import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  ControllerButtonMenu,
  ControllerMenuItem,
  SxProps,
} from '@bymason-private/legacy-pattern-components';
import {
  DangrousNotificationType,
  NotificationType,
  NotifyType,
  useNotifyDevices,
} from '@bymason/controller-api-hooks';

import { useToast } from '../../providers';
import { ActionConfirmationModal } from './action-confirmation-dialog';

export function DeviceActionsButton({
  deviceId,
  buttonLable,
  buttonSx,
  size = 'medium',
}: DeviceActionsButtonProps) {
  const { authToken = '' } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();

  const [showActionConfirmation, setShowActionConfirmation] = useState(false);
  const [pendingNotifyType, setPendingNotifyType] = useState<NotifyType | null>(null);

  const [notifyDevices, , notifyError, notifyBusy] = useNotifyDevices(authToken);

  const handleNotifyDevices = useCallback(
    (type: NotificationType) => () => {
      if (deviceId) {
        const selectedDeviceIds = [deviceId];
        notifyDevices({ type, devices: selectedDeviceIds });
        setPendingNotifyType(type);
      }
    },
    [deviceId, notifyDevices]
  );

  const handleDangerousNotifyDevices = useCallback(
    (type: DangrousNotificationType) => {
      if (deviceId) {
        const selectedDeviceIds = [deviceId];
        notifyDevices({ type, devices: selectedDeviceIds });
        setPendingNotifyType(type);
      }
    },
    [deviceId, notifyDevices]
  );

  const handleShowActionConfirmationDialog = () => {
    setShowActionConfirmation(true);
  };

  const handleHideActionConfirmationDialog = () => {
    setShowActionConfirmation(false);
  };

  useEffect(() => {
    const errorMessage =
      !notifyBusy && notifyError && pendingNotifyType ? `Failed to send ${pendingNotifyType} command` : '';

    if (errorMessage) {
      setToastSeverity('error');
      setToastMessage(errorMessage);
      setPendingNotifyType(null);
    }
  }, [notifyBusy, notifyError, pendingNotifyType, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (!notifyBusy && !notifyError && pendingNotifyType) {
      setToastSeverity('success');
      setToastMessage(`${pendingNotifyType} command sent`);
      setPendingNotifyType(null);
    }
  }, [notifyBusy, notifyError, pendingNotifyType, setToastMessage, setToastSeverity]);

  return (
    <>
      <ControllerButtonMenu
        sx={buttonSx}
        buttonText={buttonLable ?? 'Actions'}
        variant='outlined'
        size={size}
      >
        <ControllerMenuItem
          onClick={handleNotifyDevices(NotificationType.Refresh)}
          disabled={!deviceId || pendingNotifyType === NotificationType.Refresh}
        >
          Ping
        </ControllerMenuItem>
        <ControllerMenuItem
          onClick={handleNotifyDevices(NotificationType.Locate)}
          disabled={!deviceId || pendingNotifyType === NotificationType.Locate}
        >
          Locate
        </ControllerMenuItem>
        <ControllerMenuItem
          onClick={handleNotifyDevices(NotificationType.Update)}
          disabled={!deviceId || pendingNotifyType === NotificationType.Update}
        >
          Check for updates
        </ControllerMenuItem>
        <ControllerMenuItem
          value='more'
          disabled={!deviceId || showActionConfirmation}
          onClick={handleShowActionConfirmationDialog}
        >
          More actions...
        </ControllerMenuItem>
      </ControllerButtonMenu>
      <ActionConfirmationModal
        open={showActionConfirmation}
        onClose={handleHideActionConfirmationDialog}
        onSubmit={handleDangerousNotifyDevices}
      />
    </>
  );
}

interface DeviceActionsButtonProps {
  deviceId: string;
  buttonLable?: string;
  buttonSx?: SxProps;
  size?: 'small' | 'medium';
}
